import type { CompPoints, User } from '@/api/auth';
import type { CMSGroupsConfig, MissionBlockI } from '@/api/cms/loyalty';
import { BonusStage } from '@/api/common';
import { filterByGroup, parseGroup } from '@/helpers/cms';
import { parseSoftSwissFormatDate } from '@/helpers/dateTime';
import { isBonusIssuedAfterDays } from '@/modules/common/helpers/isBonusIssuedAfterDays';
import { BonusComputedStatus } from '@/modules/sportsbook';
import {
  type LoyaltyLevel,
  type LoyaltyLevelPrizeLabel,
  type LoyaltyLevelPrizeValues,
  loyaltyLevels,
} from './data';

export const MISSION_PARAM = 'missions[]';
export const MIN_PROGRESS = 0;
export const MAX_PROGRESS = 100;
export const ALMOST_COMPLETE_PROGRESS = 98;
export const NEWBIES_IDENTIFIER = 'welcome-newbies';
export const SPORTS_IDENTIFIER = 'sports-arena';
const BONUS_EXPIRATION_DAYS = 14;

export enum GroupsIncludeLoyalty {
  INVITE_FRIEND = 'WN_13',
}

export enum MissionMode {
  DEFAULT = 0,
  ACTIVE = 1,
  DONE = 2,
}

export interface LoyaltyDataI {
  level: number;
  nextLevel: number;
  prizeValues?: LoyaltyLevelPrizeValues;
  prizeLabel: LoyaltyLevelPrizeLabel;
  nextPrizeValues?: LoyaltyLevelPrizeValues;
  nextPrizeLabel: LoyaltyLevelPrizeLabel;
  currentCompPoints: number;
  fromCompPoints: number;
  toCompPoints: number;
}

export const detectLoyaltyData =
  (levelsData: LoyaltyLevel[]) =>
  (compPoints?: CompPoints): LoyaltyDataI => {
    const points = compPoints?.persistent?.points || 0;

    const currentIndexPoint = levelsData.findIndex(
      ({ cp }, index) =>
        points &&
        cp <= points &&
        (!levelsData[index + 1] || points < levelsData[index + 1].cp)
    );

    const {
      level = 0,
      prizeLabel,
      prizeValues,
      cp = 0,
    } = levelsData[currentIndexPoint] || {};
    const nextLevel = levelsData[currentIndexPoint + 1];

    return {
      level,
      nextLevel: nextLevel?.level || level + 1,
      prizeLabel,
      prizeValues,
      nextPrizeLabel: nextLevel?.prizeLabel,
      nextPrizeValues: nextLevel?.prizeValues,
      currentCompPoints: points,
      fromCompPoints: cp,
      toCompPoints: nextLevel?.cp || points,
    };
  };

export const generateLoyaltyData = detectLoyaltyData(loyaltyLevels);

type CheckByUserGroupsArgs = Pick<
  MissionBlockI,
  keyof CMSGroupsConfig | 'showGuestToggle' | 'onlyGuestAvailable'
>;

export const checkByUserGroups =
  (user?: User) =>
  ({
    groupsIncludeMode,
    groupsInclude,
    groupsExcludeMode,
    groupsExclude,
    showGuestToggle,
    onlyGuestAvailable,
  }: CheckByUserGroupsArgs) => {
    if (!(user && user?.statuses)) return onlyGuestAvailable || showGuestToggle;

    if (onlyGuestAvailable) return false;

    const parsedGroupsInclude = parseGroup(groupsInclude);
    const parsedGroupsExclude = parseGroup(groupsExclude);

    const includeResult =
      !parsedGroupsInclude.length ||
      filterByGroup(parsedGroupsInclude, groupsIncludeMode, user.statuses);

    const excludeResult = !(
      parsedGroupsExclude.length &&
      filterByGroup(parsedGroupsExclude, groupsExcludeMode, user.statuses)
    );

    return includeResult && excludeResult;
  };

export enum MessageType {
  Warning = 'warning',
  Success = 'success',
}

interface IBonusProps {
  translationKey?: string;
  messageType?: MessageType;
}

interface IBonusConfig {
  bonusStage?: BonusStage;
  bonusCreatedAt?: string;
  sportBonusCreatedAt?: string;
  canIssueBonuses?: boolean;
  isExchangeButton?: boolean;
  isSportsBlock?: boolean;
  isCombinedBonus?: boolean;
  sportBonusStage?: BonusComputedStatus;
}

export const getBonusConfig = ({
  bonusStage,
  bonusCreatedAt,
  sportBonusCreatedAt,
  canIssueBonuses,
  isExchangeButton,
  isSportsBlock,
  isCombinedBonus,
  sportBonusStage,
}: IBonusConfig): IBonusProps | null => {
  const isCasinoBonusLate =
    bonusStage &&
    [BonusStage.HandleBets, BonusStage.Issued].includes(bonusStage) &&
    isBonusIssuedAfterDays(bonusCreatedAt, BONUS_EXPIRATION_DAYS);

  const isSportsBonusLate =
    sportBonusStage &&
    [BonusComputedStatus.Active, BonusComputedStatus.Available].includes(
      sportBonusStage
    ) &&
    isBonusIssuedAfterDays(sportBonusCreatedAt, BONUS_EXPIRATION_DAYS);

  if (isExchangeButton) {
    return {
      translationKey: 'loyalty:notification.email',
      messageType: MessageType.Success,
    };
  }

  if (bonusStage === BonusStage.Activated) {
    return {
      translationKey: 'loyalty:notification.activated',
      messageType: MessageType.Success,
    };
  }

  if (bonusStage === BonusStage.Canceled) {
    return {
      translationKey: 'loyalty:notification.canceled',
      messageType: MessageType.Warning,
    };
  }

  if (isCasinoBonusLate || isSportsBonusLate) {
    return {
      translationKey: 'loyalty:notification.expiredLate',
      messageType: MessageType.Warning,
    };
  }

  if (
    bonusStage === BonusStage.Expired ||
    sportBonusStage === BonusComputedStatus.Expired
  ) {
    const isExpiredLate = isBonusIssuedAfterDays(
      bonusCreatedAt,
      BONUS_EXPIRATION_DAYS
    );
    return {
      translationKey: isExpiredLate
        ? 'loyalty:notification.expiredLate'
        : 'loyalty:notification.expired',
      messageType: MessageType.Warning,
    };
  }

  if (bonusStage === BonusStage.Lost) {
    return {
      translationKey: 'loyalty:notification.bonus.lost',
      messageType: MessageType.Success,
    };
  }

  if (
    bonusStage === BonusStage.Finished ||
    sportBonusStage === BonusComputedStatus.Over
  ) {
    return {
      translationKey: 'loyalty:notification.finished',
      messageType: MessageType.Success,
    };
  }

  if (sportBonusStage === BonusComputedStatus.Unknown) {
    return {
      translationKey: 'loyalty:notification.unknown',
      messageType: MessageType.Warning,
    };
  }

  if (isCombinedBonus && bonusStage) {
    return {
      translationKey: 'loyalty:notification.combined',
      messageType: MessageType.Success,
    };
  }

  if (!(canIssueBonuses || bonusStage || isSportsBlock)) {
    return {
      translationKey: 'loyalty:notification.bonus.disable',
      messageType: MessageType.Warning,
    };
  }

  return null;
};

export const isBonusInactive = (
  bonusStage?: BonusStage,
  sportBonusStage?: BonusComputedStatus
) => {
  if (bonusStage) {
    return [
      BonusStage.Finished,
      BonusStage.Expired,
      BonusStage.Canceled,
      BonusStage.Lost,
    ].includes(bonusStage);
  }
  if (sportBonusStage) {
    return [
      BonusComputedStatus.Expired,
      BonusComputedStatus.Over,
      BonusComputedStatus.Unknown,
    ].includes(sportBonusStage);
  }
  return false;
};

export const WELCOME_NEWBIES_PROGRESS_CUTOFF_DATE = '2025-11-12T00:00:00.000Z';

export const shouldShowNewbiesProgress = (user?: User): boolean => {
  if (!user?.createdAt) return false;

  const userCreatedAt = parseSoftSwissFormatDate(user.createdAt);
  const cutoffDate = new Date(WELCOME_NEWBIES_PROGRESS_CUTOFF_DATE);

  // Validate dates to handle iOS Safari parsing issues
  const userTime = userCreatedAt.getTime();
  const cutoffTime = cutoffDate.getTime();

  // If date parsing failed (Invalid Date), return true as safe fallback
  if (isNaN(userTime) || isNaN(cutoffTime)) {
    return true;
  }

  return userTime >= cutoffTime;
};
